import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { API, authHeaders, toastErr } from "@/components/admin/AdminPage";

const inputCls = "w-full rounded-lg bg-white border-2 border-ink px-3 py-2 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow";

export default function AdminFormFields({ token }) {
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/form-config`).then((r) => setConfig(r.data)).catch((e) => toastErr(e, "Could not load form config"));
  }, []);

  if (!config) return <p className="text-plum font-medium text-sm">Loading…</p>;

  const patchField = (si, fi, patch) =>
    setConfig((c) => {
      const steps = c.steps.map((s, i) =>
        i !== si ? s : { ...s, fields: s.fields.map((f, j) => (j !== fi ? f : { ...f, ...patch })) });
      return { ...c, steps };
    });

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/form-config`, config, authHeaders(token));
      toast.success("Order form updated — live on the store now");
    } catch (e) { toastErr(e); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-form-fields-panel">
      <div className="space-y-6 max-w-3xl mx-auto">
        {config.steps.map((s, si) => (
          <div key={si} className="rounded-2xl bg-white border-[3px] border-ink shadow-[5px_5px_0_#2D1A32] p-6">
            <h3 className="font-display font-bold text-lg mb-4">Step {si + 1}: {s.title}</h3>
            <div className="space-y-4">
              {s.fields.map((f, fi) => (
                <div key={f.id} className="rounded-xl bg-cream border-2 border-ink/20 p-4" data-testid={`form-field-editor-${f.id}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block font-head font-bold text-[11px] text-plum mb-1">Label</label>
                      <input value={f.label} onChange={(e) => patchField(si, fi, { label: e.target.value })} className={inputCls} />
                    </div>
                    {f.type === "select" && (
                      <div>
                        <label className="block font-head font-bold text-[11px] text-plum mb-1">Options (comma separated)</label>
                        <input value={(f.options || []).join(", ")} onChange={(e) => patchField(si, fi, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })} className={inputCls} />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-5 mt-3">
                    <label className="flex items-center gap-2 text-xs font-head font-bold text-plum cursor-pointer">
                      <input type="checkbox" checked={f.visible !== false} onChange={(e) => patchField(si, fi, { visible: e.target.checked })} className="w-4 h-4 accent-pink" />
                      Show field
                    </label>
                    <label className="flex items-center gap-2 text-xs font-head font-bold text-plum cursor-pointer">
                      <input type="checkbox" checked={!!f.required} onChange={(e) => patchField(si, fi, { required: e.target.checked })} className="w-4 h-4 accent-pink" />
                      Required
                    </label>
                    <span className="ml-auto font-mono text-[10px] text-lilac">{f.id} · {f.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button data-testid="form-config-save-button" onClick={save} disabled={busy}
        className="btn-pop mt-6 rounded-full bg-pink text-white font-head font-bold px-8 py-3 text-sm border-2 border-ink shadow-[4px_4px_0_#2D1A32] disabled:opacity-50 flex items-center gap-2">
        {busy && <Loader2 size={15} className="animate-spin" />} Save Form Changes
      </button>
    </div>
  );
}
